import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  Star,
  Award,
  ThumbsUp,
  LayoutGrid,
  ArrowRight,
  X,
  Clapperboard,
} from 'lucide-react';
import { Container } from '../../components/styled/Container';
import { PageLayout } from '../../components/styled/PageLayout';
import { fetchMediaDetail } from '../../store/actions/mediaActions';
import { fetchSimilarList, submitSimilarityVote } from '../../store/actions/similarActions';
import { fetchReviews } from '../../store/actions/reviewActions';
import { searchMulti, clearSearch } from '../../store/actions/searchActions';
import { submitSimilarSuggestion, resetSuggestion } from '../../store/actions/suggestionActions';
import { detailPath, similarPath, parseIdSlug } from '../../utils/paths';
import { buildMediaSeo } from '../../utils/pageSeo';
import { regionFromPathname } from '../../utils/regionFromPath';
import { REGION_HREFLANG } from '../../config/publicRegions';
import SEO from '../../components/seo/SEO';
import { useI18n } from '../../hooks/useI18n';
import { useAdDevice } from '../../hooks/useAdDevice';
import StaticAdBanner from '../../components/ads/StaticAdBanner';
import AdSidebar from '../../components/ads/AdSidebar';
import AdCarousel from '../../components/ads/AdCarousel';
import { bannerAds, sidebarAds, carouselAds } from '../../data/staticAds';
import { useSiteAdsManaged } from '../../hooks/useSiteAdsManaged';
import { fetchAdPlacement, trackAdClick, trackAdImpression } from '../../store/actions/adActions';
import { selectAdPlacementFirst, selectAdPlacementList } from '../../store/selectors/adSelectors';
import { managedPlacementToAd, pickStaticFallback } from '../../utils/adMapper';
import { selectAdsStaticEnabled } from '../../store/reducers/sitePublicReducer';
import { makeImpressionKey, shouldTrackImpression } from '../../utils/adTracking';
import MediaOffers from '../../components/media/MediaOffers';
import CrawlerSeoContent from '../../components/seo/CrawlerSeoContent';
import RichContent from '../../components/content/RichContent';
import { extractFaqJsonLd, humanizeSlug } from '../../utils/richContent';

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin-bottom: 1.5rem;
  text-decoration: none;
  &:hover {
    color: ${({ theme }) => theme.colors.foreground};
  }
`;

const Card = styled(motion.div)`
  background: ${({ theme }) => theme.colors.card};
  border-radius: ${({ theme }) => theme.radiusSm};
  box-shadow: ${({ theme }) => theme.shadowCard};
  overflow: hidden;
`;

const AccentBar = styled.div`
  height: 6px;
  background: ${({ theme }) => theme.colors.primary};
`;

const CardInner = styled.div`
  padding: 2rem;
  @media (min-width: 768px) {
    padding: 2.5rem;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
  }
`;

const LogoLarge = styled.div`
  width: 5rem;
  height: 5rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: rgba(0, 48, 135, 0.1);
  background-image: ${({ $url }) => ($url ? `url(${$url})` : 'none')};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 700;
  font-size: ${({ theme }) => theme.fontClamp['3xl']};
  flex-shrink: 0;
`;

const HeaderInfo = styled.div`
  flex: 1;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const SiteTitle = styled.h1`
  font-size: ${({ theme }) => theme.fontClamp['3xl']};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foreground};
  margin: 0;
`;

const ScoreBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.75rem;
  background: rgba(234, 179, 8, 0.1);
  color: ${({ theme }) => theme.colors.gold};
  font-size: ${({ theme }) => theme.fontClamp.xs};
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-radius: ${({ theme }) => theme.radiusSm};
`;

const MetaLine = styled.p`
  margin: 0.25rem 0 0 0;
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 600;
`;

const Description = styled.p`
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin: 0.75rem 0 0 0;
  max-width: 42rem;
  line-height: 1.6;
`;

const ViewDetailsBtn = styled(Link)`
  padding: 0.75rem 1.5rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.primaryForeground};
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontClamp.sm};
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  transition: opacity 0.2s;
  &:hover {
    opacity: 0.9;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-top: 2rem;
  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const StatBox = styled.div`
  padding: 1rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ $bg }) => $bg};
`;

const StatLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: ${({ theme }) => theme.fontClamp.xs};
  font-weight: 500;
  margin-bottom: 0.25rem;
  color: ${({ $color }) => $color};
`;

const StatValue = styled.p`
  font-variant-numeric: tabular-nums;
  font-size: ${({ theme }) => theme.fontClamp.xl};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.foreground};
  margin: 0;
`;

const TagsWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1.5rem;
`;

const Tag = styled.span`
  padding: 0.375rem 1rem;
  background: rgba(220, 38, 38, 0.1);
  color: ${({ theme }) => theme.colors.crimson};
  font-size: ${({ theme }) => theme.fontClamp.sm};
  font-weight: 500;
  border-radius: ${({ theme }) => theme.radiusSm};
`;

const SimilarTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontClamp['2xl']};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foreground};
  margin: ${({ $afterHero }) => ($afterHero ? '1.5rem 0 1.25rem' : '3rem 0 1.5rem')};
`;

const SimilarList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const SimilarCard = styled(motion.div)`
  display: flex;
  flex-direction: column;
  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    gap: 1.5rem;
  }
  background: ${({ theme }) => theme.colors.card};
  border-radius: ${({ theme }) => theme.radiusSm};
  box-shadow: ${({ theme }) => theme.shadowCard};
  padding: 1.25rem;
  transition: box-shadow 0.2s;
  &:hover {
    box-shadow: ${({ theme }) => theme.shadowCardHover};
  }
`;

const SimilarCardLeft = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  gap: 1rem;
  align-items: flex-start;
`;

const SimilarCardAvatar = styled.div`
  width: 3rem;
  height: 3rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: rgba(0, 48, 135, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-weight: 700;
  color: var(--primary);
`;

const SimilarCardPoster = styled.img`
  width: 3rem;
  height: 3rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  object-fit: cover;
  flex-shrink: 0;
  background: rgba(0, 48, 135, 0.1);
`;

const IntroSection = styled(RichContent)`
  margin-top: 1.5rem;
`;

const IntroHeading = styled.h2`
  font-size: ${({ theme }) => theme.fontClamp.xl};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foreground};
  margin: 2rem 0 0.75rem;
`;

const SimilarCardTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontClamp.lg};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foreground};
  margin: 0;
`;

const SimilarCardDesc = styled.p`
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin: 0.25rem 0 0 0;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const SimilarCardTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const SimilarCardTag = styled.span`
  padding: 0.2rem 0.5rem;
  background: rgba(128, 0, 0, 0.1);
  color: ${({ theme }) => theme.colors.maroon};
  font-size: ${({ theme }) => theme.fontClamp.xs};
  font-weight: 500;
  border-radius: ${({ theme }) => theme.radiusSm};
`;

const SimilarCardRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.75rem;
  margin-top: 1rem;
  @media (min-width: 768px) {
    margin-top: 0;
    align-items: flex-end;
    flex-shrink: 0;
  }
`;

const HowSimilarLabel = styled.span`
  font-size: ${({ theme }) => theme.fontClamp.xs};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const StarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const StarBtn = styled.button`
  padding: 0.125rem;
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.mutedForeground};
  transition: color 0.2s;
  &:hover {
    color: ${({ theme }) => theme.colors.gold};
  }
  &.filled {
    color: ${({ theme }) => theme.colors.gold};
  }
`;

const RankBadge = styled.span`
  font-size: ${({ theme }) => theme.fontClamp.xs};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  text-align: right;
`;

const ViewSimilarLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.primaryForeground};
  font-size: ${({ theme }) => theme.fontClamp.sm};
  font-weight: 600;
  white-space: nowrap;
  text-decoration: none;
  transition: opacity 0.2s;
  &:hover {
    opacity: 0.9;
  }
`;

const ModalOverlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;

const ModalCard = styled(motion.div)`
  background: ${({ theme }) => theme.colors.card};
  border-radius: ${({ theme }) => theme.radiusSm};
  box-shadow: ${({ theme }) => theme.shadowCard};
  max-width: 28rem;
  width: 100%;
  padding: 1.5rem;
  position: relative;
`;

const ModalTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontClamp.lg};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foreground};
  margin: 0 0 1rem 0;
  padding-right: 2rem;
`;

const ModalClose = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 2rem;
  height: 2rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.mutedForeground};
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover {
    background: ${({ theme }) => theme.colors.muted};
    color: ${({ theme }) => theme.colors.foreground};
  }
`;

const ModalRatingLabel = styled.p`
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin: 0 0 0.5rem 0;
`;

const ModalReasonLabel = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.fontClamp.sm};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.foreground};
  margin: 1rem 0 0.5rem 0;
`;

const ModalTextarea = styled.textarea`
  width: 100%;
  min-height: 4rem;
  padding: 0.75rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  border: 2px solid ${({ theme }) => theme.colors.border};
  font-size: ${({ theme }) => theme.fontClamp.sm};
  font-family: inherit;
  color: ${({ theme }) => theme.colors.foreground};
  background: ${({ theme }) => theme.colors.background};
  resize: vertical;
  margin-bottom: 0.5rem;
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const ModalCharCount = styled.span`
  font-size: ${({ theme }) => theme.fontClamp.xs};
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const ModalActions = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1.25rem;
`;

const ModalBtn = styled.button`
  padding: 0.5rem 1.25rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  font-size: ${({ theme }) => theme.fontClamp.sm};
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;
  border: none;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ModalSubmitBtn = styled(ModalBtn)`
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.primaryForeground};
  &:hover:not(:disabled) {
    opacity: 0.9;
  }
`;

const ModalCancelBtn = styled(ModalBtn)`
  background: ${({ theme }) => theme.colors.muted};
  color: ${({ theme }) => theme.colors.foreground};
  &:hover {
    opacity: 0.9;
  }
`;

const ModalError = styled.p`
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: ${({ theme }) => theme.colors.crimson};
  margin: 0.5rem 0 0 0;
`;

const SuggestSection = styled.div`
  margin-top: 3rem;
  padding: 1.5rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.card};
  box-shadow: ${({ theme }) => theme.shadowCard};
`;

const SuggestTitle = styled.h3`
  margin: 0 0 0.5rem;
  font-size: ${({ theme }) => theme.fontClamp.lg};
`;

const Input = styled.input`
  width: 100%;
  max-width: 360px;
  padding: 0.65rem 0.85rem;
  margin: 0.5rem 0.5rem 0.5rem 0;
  border-radius: ${({ theme }) => theme.radiusSm};
  border: 2px solid ${({ theme }) => theme.colors.border};
  font-size: ${({ theme }) => theme.fontClamp.sm};
`;

const PickRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 0.75rem 0;
`;

const PickBtn = styled.button`
  padding: 0.4rem 0.65rem;
  font-size: ${({ theme }) => theme.fontClamp.xs};
  border-radius: ${({ theme }) => theme.radiusSm};
  border: 2px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.muted};
  cursor: pointer;
`;

const SmallBtn = styled.button`
  padding: 0.5rem 1rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  border: none;
  background: ${({ theme }) => theme.colors.crimson};
  color: ${({ theme }) => theme.colors.crimsonForeground};
  font-weight: 600;
  cursor: pointer;
`;

const REASON_MAX = 500;

function kindLabel(category) {
  if (category === 'anime_tv') return 'Anime TV series';
  if (category === 'anime_movie') return 'Anime movie';
  if (category === 'tv') return 'TV series';
  return 'Movie';
}

function itemTags(item) {
  if (item.tags && item.tags.length) return item.tags;
  if (item.genreSlugs && item.genreSlugs.length) return item.genreSlugs.map(humanizeSlug);
  return [];
}

function itemOverview(item, fallback) {
  return item.overview || fallback;
}

/** Derive a display title from URL slug when hero is missing */
function titleFromSlug(slug) {
  if (!slug || typeof slug !== 'string') return '';
  return slug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function MediaSimilarPage({ category }) {
  const { idSlug } = useParams();
  const { id, slug } = useMemo(() => parseIdSlug(idSlug), [idSlug]);
  const baseId = useMemo(() => parseInt(id, 10), [id]);
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const region = regionFromPathname(pathname);
  const pre = region || '';
  const { t, locale } = useI18n();
  const serverLanguage = REGION_HREFLANG[region] || 'en-US';

  const adDomain = useMemo(() => (typeof window !== 'undefined' ? window.location.hostname : ''), []);
  const adLocale = (locale || 'en').toLowerCase();
  const adDevice = useAdDevice();
  const { sitePublicLoaded, adsManagedEnabled } = useSiteAdsManaged();
  const adsStaticEnabled = useSelector(selectAdsStaticEnabled);

  const managedSimilarBanner = useSelector((s) => selectAdPlacementFirst(s, 'similar', 'inline-banner'));
  const managedSimilarSidebars = useSelector((s) => selectAdPlacementList(s, 'similar', 'sidebar'));
  const managedSimilarCarousel = useSelector((s) => selectAdPlacementList(s, 'similar', 'carousel'));

  useEffect(() => {
    if (!sitePublicLoaded || !adsManagedEnabled) return;
    const baseParams = { device: adDevice, domain: adDomain, locale: adLocale };
    dispatch(
      fetchAdPlacement({
        page: 'similar',
        placement: 'inline-banner',
        params: { ...baseParams, page: 'similar', placement: 'inline-banner', limit: 1 },
      }),
    );
    dispatch(
      fetchAdPlacement({
        page: 'similar',
        placement: 'sidebar',
        params: { ...baseParams, page: 'similar', placement: 'sidebar', limit: 2 },
      }),
    );
    dispatch(
      fetchAdPlacement({
        page: 'similar',
        placement: 'carousel',
        params: { ...baseParams, page: 'similar', placement: 'carousel', limit: 6 },
      }),
    );
  }, [dispatch, adDevice, adDomain, adLocale, sitePublicLoaded, adsManagedEnabled]);

  useEffect(() => {
    if (!sitePublicLoaded || !adsManagedEnabled || !managedSimilarBanner?._id) return;
    const key = makeImpressionKey({
      campaign: managedSimilarBanner._id,
      page: 'similar',
      placement: 'inline-banner',
      device: adDevice,
      domain: adDomain,
    });
    if (shouldTrackImpression(key)) {
      dispatch(
        trackAdImpression({
          campaign: managedSimilarBanner._id,
          page: 'similar',
          placement: 'inline-banner',
          device: adDevice,
          domain: adDomain,
          locale: adLocale,
        }),
      );
    }
  }, [dispatch, managedSimilarBanner, adDevice, adDomain, adLocale, sitePublicLoaded, adsManagedEnabled]);

  useEffect(() => {
    if (!sitePublicLoaded || !adsManagedEnabled) return;
    managedSimilarSidebars.forEach((item) => {
      if (!item?._id) return;
      const key = makeImpressionKey({
        campaign: item._id,
        page: 'similar',
        placement: 'sidebar',
        device: adDevice,
        domain: adDomain,
      });
      if (shouldTrackImpression(key)) {
        dispatch(
          trackAdImpression({
            campaign: item._id,
            page: 'similar',
            placement: 'sidebar',
            device: adDevice,
            domain: adDomain,
            locale: adLocale,
          }),
        );
      }
    });
  }, [dispatch, managedSimilarSidebars, adDevice, adDomain, adLocale, sitePublicLoaded, adsManagedEnabled]);

  const similarBannerAd = useMemo(
    () => managedPlacementToAd(managedSimilarBanner, pickStaticFallback(adsStaticEnabled, bannerAds[0])),
    [managedSimilarBanner, adsStaticEnabled],
  );
  const similarSidebarLeft = useMemo(
    () => managedPlacementToAd(managedSimilarSidebars[0], pickStaticFallback(adsStaticEnabled, sidebarAds[0])),
    [managedSimilarSidebars, adsStaticEnabled],
  );
  const similarSidebarRight = useMemo(
    () =>
      managedPlacementToAd(
        managedSimilarSidebars[1],
        pickStaticFallback(adsStaticEnabled, sidebarAds[1] ?? sidebarAds[0]),
      ),
    [managedSimilarSidebars, adsStaticEnabled],
  );
  const similarCarouselAds = useMemo(() => {
    if (managedSimilarCarousel.length) {
      return managedSimilarCarousel.map((x, i) =>
        managedPlacementToAd(x, pickStaticFallback(adsStaticEnabled, carouselAds[i] || carouselAds[0])),
      );
    }
    return adsStaticEnabled ? carouselAds : [];
  }, [managedSimilarCarousel, adsStaticEnabled]);

  const onSimilarBannerClick = useCallback(() => {
    if (!sitePublicLoaded || !adsManagedEnabled || !managedSimilarBanner?._id) return;
    dispatch(
      trackAdClick({
        campaign: managedSimilarBanner._id,
        page: 'similar',
        placement: 'inline-banner',
        device: adDevice,
        domain: adDomain,
        locale: adLocale,
      }),
    );
  }, [dispatch, managedSimilarBanner, adDevice, adDomain, adLocale, sitePublicLoaded, adsManagedEnabled]);

  const onSimilarSidebarClick = useCallback(
    (campaignId) => () => {
      if (!sitePublicLoaded || !adsManagedEnabled || !campaignId) return;
      dispatch(
        trackAdClick({
          campaign: campaignId,
          page: 'similar',
          placement: 'sidebar',
          device: adDevice,
          domain: adDomain,
          locale: adLocale,
        }),
      );
    },
    [dispatch, adDevice, adDomain, adLocale, sitePublicLoaded, adsManagedEnabled],
  );

  const onSimilarCarouselVisible = useCallback(
    (visibleAd) => {
      if (!sitePublicLoaded || !adsManagedEnabled || !visibleAd?.managedCampaignId) return;
      const key = makeImpressionKey({
        campaign: visibleAd.managedCampaignId,
        page: 'similar',
        placement: 'carousel',
        device: adDevice,
        domain: adDomain,
      });
      if (shouldTrackImpression(key)) {
        dispatch(
          trackAdImpression({
            campaign: visibleAd.managedCampaignId,
            page: 'similar',
            placement: 'carousel',
            device: adDevice,
            domain: adDomain,
            locale: adLocale,
          }),
        );
      }
    },
    [dispatch, adDevice, adDomain, adLocale, sitePublicLoaded, adsManagedEnabled],
  );

  const onSimilarCarouselClick = useCallback(
    (_e, visibleAd) => {
      if (!sitePublicLoaded || !adsManagedEnabled || !visibleAd?.managedCampaignId) return;
      dispatch(
        trackAdClick({
          campaign: visibleAd.managedCampaignId,
          page: 'similar',
          placement: 'carousel',
          device: adDevice,
          domain: adDomain,
          locale: adLocale,
        }),
      );
    },
    [dispatch, adDevice, adDomain, adLocale, sitePublicLoaded, adsManagedEnabled],
  );

  const { detail: hero, loading: heroLoad, error: heroErr } = useSelector((s) => s.media);
  const { results: similar, loading: simLoad, voteSubmittingId } = useSelector((s) => s.similar);
  const { list: reviews } = useSelector((s) => s.reviews);
  const { results: searchResults, loading: searchLoad } = useSelector((s) => s.search);
  const { message: suggMsg, error: suggErr } = useSelector((s) => s.suggestion);

  const fallbackTitle = titleFromSlug(slug) || (hero?.title) || `${t('common.title', 'Title')} #${baseId}`;
  const effectiveHero = hero || {
    title: fallbackTitle,
    overview: t('common.noOverview', 'No overview yet.'),
    posterUrl: null,
    voteAverage: null,
    releaseDate: '',
    genres: [],
  };

  const [voteModal, setVoteModal] = useState(null);
  const [voteReason, setVoteReason] = useState('');
  const [voteSubmitting, setVoteSubmitting] = useState(false);
  const [voteError, setVoteError] = useState('');
  const [suggestQ, setSuggestQ] = useState('');
  const [picked, setPicked] = useState(null);
  const [suggestReason, setSuggestReason] = useState('');

  useEffect(() => {
    if (!Number.isFinite(baseId)) return;
    dispatch(fetchMediaDetail(category, baseId, { language: serverLanguage }));
    dispatch(fetchSimilarList(category, baseId, { language: serverLanguage }));
    dispatch(fetchReviews(category, baseId));
    dispatch(resetSuggestion());
    return () => dispatch(clearSearch());
  }, [dispatch, category, baseId, serverLanguage]);

  const homeTo = pre ? `/${pre}` : '/';

  const openVoteModal = (similarItem, rating) => {
    setVoteModal({ item: similarItem, rating });
    setVoteReason('');
    setVoteError('');
  };

  const closeVoteModal = () => {
    if (!voteSubmitting) setVoteModal(null);
  };

  const handleSubmitVote = async () => {
    if (!voteModal) return;
    setVoteSubmitting(true);
    setVoteError('');
    const { item, rating } = voteModal;
    await dispatch(
      submitSimilarityVote(category, baseId, {
        alternativeCategory: item.category,
        alternativeId: item.id,
        rating,
        reason: voteReason.trim() || undefined,
        baseTitle: title,
        altTitle: item.title,
        language: serverLanguage,
      }),
    );
    setVoteSubmitting(false);
    setVoteModal(null);
    setVoteReason('');
  };

  const onSearchSuggest = (e) => {
    e.preventDefault();
    if (suggestQ.trim()) dispatch(searchMulti(suggestQ.trim(), 1, { language: serverLanguage }));
  };

  const onSubmitSuggestion = (e) => {
    e.preventDefault();
    if (!picked) return;
    dispatch(
      submitSimilarSuggestion(category, baseId, {
        similarTmdbKind: picked.media_type,
        similarTmdbId: picked.id,
        reason: suggestReason,
        baseTitle: title,
        suggestedTitle: picked.label,
      }),
    );
    setPicked(null);
    setSuggestReason('');
  };

  const title = hero?.similarSeo?.headline?.trim() || effectiveHero.title;
  const introHtml = hero?.similarSeo?.content?.trim() || '';
  const tmdbScore = effectiveHero?.voteAverage != null ? Number(effectiveHero.voteAverage).toFixed(1) : null;
  const reviewCount = Array.isArray(reviews) ? reviews.length : 0;
  const reviewAvg = useMemo(() => {
    if (!reviewCount) return null;
    const sum = reviews.reduce((acc, r) => acc + (Number(r?.rating) || 0), 0);
    return (sum / reviewCount).toFixed(1);
  }, [reviews, reviewCount]);
  const voteStats = useMemo(() => {
    let weighted = 0;
    let totalVotes = 0;
    (Array.isArray(similar) ? similar : []).forEach((s) => {
      const avg = Number(s?.similarityVoteStats?.averageRating || 0);
      const count = Number(s?.similarityVoteStats?.voteCount || 0);
      if (count > 0) {
        weighted += avg * count;
        totalVotes += count;
      }
    });
    const voteScore = totalVotes > 0 ? (weighted / totalVotes).toFixed(1) : null;
    return { voteScore, totalVotes };
  }, [similar]);
  const year = effectiveHero.releaseDate ? String(effectiveHero.releaseDate).slice(0, 4) : '';
  const detailTo = detailPath(category, baseId, title, pre, hero?.seo?.slug || '');
  const pageSeo = buildMediaSeo({
    hero: hero || effectiveHero,
    category,
    baseId,
    regionPrefix: pre,
    pathSuffix: '/similar',
    similarSeo: hero?.similarSeo,
    fallbackTitleSuffix: t('similar.browse.metaSuffix', 'Similar Picks'),
    fallbackDescription: t(
      'media.similar.metaDescription',
      'Find similar picks, compare alternatives, and vote with the community.',
    ),
  });
  const faqJsonLd = useMemo(() => extractFaqJsonLd(introHtml), [introHtml]);

  if (!Number.isFinite(baseId)) {
    return (
      <PageLayout>
        <Container style={{ paddingTop: '3rem' }}>
          <p>{t('common.invalidTitle', 'Invalid title.')}</p>
        </Container>
      </PageLayout>
    );
  }

  if ((heroLoad || simLoad) && !hero && !slug) {
    return (
      <PageLayout>
        <Container style={{ paddingTop: '5rem', paddingBottom: '5rem' }}>
          <p style={{ color: 'var(--muted-foreground)' }}>{t('common.loading', 'Loading...')}</p>
        </Container>
      </PageLayout>
    );
  }

  return (
    <>
      <SEO
        region={region}
        path={pageSeo.path}
        canonicalOverride={pageSeo.canonicalOverride}
        title={pageSeo.title}
        description={pageSeo.description}
        keywords={pageSeo.keywords}
        noindex={pageSeo.noindex}
        image={pageSeo.image}
        jsonLd={faqJsonLd ? [faqJsonLd] : null}
        hreflangAlternates={pageSeo.hreflangAlternates}
      />
      <CrawlerSeoContent title={pageSeo.title} intro={introHtml || effectiveHero.overview} bodyParagraphs={[introHtml]} />
      <PageLayout>
      <Container style={{ paddingTop: '2.5rem', paddingBottom: '2.5rem' }}>
        <BackLink to={homeTo}>
          <ArrowLeft size={16} strokeWidth={2.5} /> {t('common.home', 'Home')}
        </BackLink>

        <Card initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <AccentBar />
          <CardInner>
            <HeaderRow>
              <LogoLarge $url={effectiveHero.posterUrl}>{!effectiveHero.posterUrl ? title.charAt(0) : ''}</LogoLarge>
              <HeaderInfo>
                <TitleRow>
                  <SiteTitle>{title}</SiteTitle>
                  {tmdbScore != null ? (
                    <ScoreBadge>
                      <Star size={12} fill="currentColor" /> TMDB {tmdbScore}/10
                    </ScoreBadge>
                  ) : null}
                  {voteStats.voteScore != null ? (
                    <ScoreBadge>
                      <Star size={12} fill="currentColor" /> {voteStats.voteScore}/5 {t('common.votes', 'votes')}
                    </ScoreBadge>
                  ) : null}
                </TitleRow>
                <MetaLine>
                  {kindLabel(category)}
                  {year ? ` · ${year}` : ''}
                </MetaLine>
                <Description>{effectiveHero.overview || t('common.noOverview', 'No overview yet.')}</Description>
              </HeaderInfo>
              <ViewDetailsBtn to={detailTo}>
                {t('top.viewDetails', 'View details')} <ArrowRight size={14} />
              </ViewDetailsBtn>
            </HeaderRow>

            <StatsGrid>
              <StatBox $bg="rgba(128,0,0,0.05)">
                <StatLabel $color="var(--maroon)">
                  <LayoutGrid size={14} strokeWidth={2.5} /> {t('common.type', 'Type')}
                </StatLabel>
                <StatValue style={{ fontSize: '1.25rem' }}>{kindLabel(category)}</StatValue>
              </StatBox>
              <StatBox $bg="rgba(234,179,8,0.05)">
                <StatLabel $color="var(--gold)">
                  <ThumbsUp size={14} strokeWidth={2.5} /> {t('common.voteScore', 'Vote score')}
                </StatLabel>
                <StatValue style={{ fontSize: '1rem' }}>
                  {voteStats.voteScore != null
                    ? `${voteStats.voteScore}/5 (${voteStats.totalVotes} ${t('common.votes', 'votes')})`
                    : t('common.noVotes', 'No votes yet')}
                </StatValue>
              </StatBox>
              <StatBox $bg="rgba(0,48,135,0.05)">
                <StatLabel $color="var(--primary)">
                  <Award size={14} strokeWidth={2.5} /> {t('similar.browse.similarBadge', 'Similar picks')}
                </StatLabel>
                <StatValue>{similar.length}</StatValue>
              </StatBox>
              <StatBox $bg="rgba(34,197,94,0.08)">
                <StatLabel $color="hsl(142,71%,45%)">
                  <Award size={14} strokeWidth={2.5} /> {t('common.reviewRating', 'Review rating')}
                </StatLabel>
                <StatValue style={{ fontSize: '1rem' }}>
                  {reviewAvg != null
                    ? `${reviewAvg}/5 (${reviewCount} ${t('common.reviews', 'reviews')})`
                    : t('common.noReviews', 'No reviews yet')}
                </StatValue>
              </StatBox>
            </StatsGrid>

            <TagsWrap>
              {(effectiveHero.genres || []).map((g) => (
                <Tag key={g.id || g.name}>{g.name}</Tag>
              ))}
            </TagsWrap>
          </CardInner>
        </Card>

        <SimilarTitle $afterHero>
          {t('media.similar.titlesSimilarTo', 'Titles similar to')} <span style={{ color: 'var(--primary)' }}>{title}</span>
        </SimilarTitle>

        <div style={{ marginBottom: '1.25rem' }}>
          <MediaOffers
            offers={hero?.similarOffers}
            category={category}
            tmdbId={baseId}
            titleKey="media.similar.offersTitle"
            titleFallback="Partner offers"
          />
        </div>

        {!similar.length && simLoad ? (
          <SimilarCard>
            <SimilarCardDesc style={{ WebkitLineClamp: 'unset' }}>
              {t('common.loading', 'Loading...')}
            </SimilarCardDesc>
          </SimilarCard>
        ) : !similar.length ? (
          <SimilarCard>
            <SimilarCardDesc style={{ WebkitLineClamp: 'unset' }}>
              {t('media.similar.empty', 'No curated similar titles yet. Search below to suggest a movie or show you think matches.')}
            </SimilarCardDesc>
          </SimilarCard>
        ) : (
          <SimilarList>
            {similar.map((s, index) => {
              const avg = s.similarityVoteStats?.averageRating ?? 0;
              const tags = itemTags(s);
              return (
                <SimilarCard
                  key={`${s.category}-${s.id}`}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <SimilarCardLeft>
                    {s.posterUrl ? (
                      <SimilarCardPoster src={s.posterUrl} alt={s.posterAlt || s.title || ''} loading="lazy" />
                    ) : (
                      <SimilarCardAvatar>{(s.title || '?').charAt(0).toUpperCase()}</SimilarCardAvatar>
                    )}
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <SimilarCardTitle>{s.title}</SimilarCardTitle>
                      <SimilarCardDesc>
                        {itemOverview(s, `${s.title} — open similar picks to explore more like this.`)}
                      </SimilarCardDesc>
                      {tags.length > 0 && (
                        <SimilarCardTags>
                          {tags.slice(0, 4).map((tag) => (
                            <SimilarCardTag key={tag}>{tag}</SimilarCardTag>
                          ))}
                        </SimilarCardTags>
                      )}
                    </div>
                  </SimilarCardLeft>
                  <SimilarCardRight>
                    <HowSimilarLabel>How similar to {title}?</HowSimilarLabel>
                    <StarRow>
                      {[1, 2, 3, 4, 5].map((n) => {
                        const filled = n <= Math.round(avg);
                        return (
                          <StarBtn
                            key={n}
                            type="button"
                            className={filled ? 'filled' : ''}
                            onClick={() => openVoteModal(s, n)}
                            aria-label={`${n} stars`}
                          >
                            <Star
                              size={20}
                              fill={filled ? 'currentColor' : 'none'}
                              stroke="currentColor"
                              strokeWidth={1.5}
                            />
                          </StarBtn>
                        );
                      })}
                    </StarRow>
                    {s.similarityVoteStats && s.similarityVoteStats.voteCount > 0 && (
                      <RankBadge>
                        #{index + 1} · {s.similarityVoteStats.averageRating}/5 (
                        {s.similarityVoteStats.voteCount} votes)
                      </RankBadge>
                    )}
                    <ViewSimilarLink to={similarPath(s.category, s.id, s.title, pre)}>
                      {t('media.similar.viewSimilar', 'View similar')} <ArrowRight size={14} />
                    </ViewSimilarLink>
                  </SimilarCardRight>
                </SimilarCard>
              );
            })}
          </SimilarList>
        )}

        <div style={{ marginTop: '1.5rem' }}>
          <StaticAdBanner ad={similarBannerAd} variant="inline" pageKey="similar" onLinkClick={onSimilarBannerClick} />
        </div>
        <div
          style={{
            marginTop: '1.5rem',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: '1rem',
            alignItems: 'start',
          }}
        >
          <AdSidebar
            ad={similarSidebarLeft}
            pageKey="similar"
            onLinkClick={onSimilarSidebarClick(similarSidebarLeft?.managedCampaignId)}
          />
          <AdSidebar
            ad={similarSidebarRight}
            pageKey="similar"
            onLinkClick={onSimilarSidebarClick(similarSidebarRight?.managedCampaignId)}
          />
        </div>

        {introHtml ? (
          <>
            <IntroHeading>{t('media.similar.introHeading', 'About these picks')}</IntroHeading>
            <IntroSection html={introHtml} />
          </>
        ) : null}

        <div style={{ marginTop: '2rem' }}>
          <AdCarousel
            ads={similarCarouselAds}
            pageKey="similar"
            onVisibleAd={onSimilarCarouselVisible}
            onAdClick={onSimilarCarouselClick}
          />
        </div>

        <SuggestSection>
          <SuggestTitle>
            <Clapperboard size={20} style={{ verticalAlign: '-3px', marginRight: '0.35rem' }} />
            {t('media.similar.suggestTitle', 'Suggest a similar title')}
          </SuggestTitle>
          <p style={{ fontSize: '0.9rem', color: 'var(--muted-foreground)', margin: '0 0 0.75rem' }}>
            {t('media.similar.suggestLead', 'Search for a title, select it, and tell us why it is similar to')} <strong>{title}</strong>.
          </p>
          <form onSubmit={onSearchSuggest}>
            <Input
              value={suggestQ}
              onChange={(e) => setSuggestQ(e.target.value)}
              placeholder={t('search.placeholderShort', 'Search movie or TV...')}
            />
            <SmallBtn type="submit" disabled={searchLoad}>
              {t('common.search', 'Search')}
            </SmallBtn>
          </form>
          <PickRow>
            {(searchResults || [])
              .filter((r) => r.media_type === 'movie' || r.media_type === 'tv')
              .map((r) => (
                <PickBtn
                  key={`${r.media_type}-${r.id}`}
                  type="button"
                  onClick={() =>
                    setPicked({
                      media_type: r.media_type,
                      id: r.id,
                      label: r.title || r.name,
                    })
                  }
                  style={{
                    borderColor: picked?.id === r.id ? 'var(--crimson)' : undefined,
                  }}
                >
                  {r.media_type === 'movie' ? t('common.movie', 'Movie') : t('common.tv', 'TV')}: {r.title || r.name}
                </PickBtn>
              ))}
          </PickRow>
          {picked && (
            <form onSubmit={onSubmitSuggestion}>
              <p style={{ fontSize: '0.85rem', margin: '0.5rem 0' }}>{t('media.similar.selected', 'Selected')}: {picked.label}</p>
              <ModalTextarea
                placeholder={t('media.similar.whySimilar', 'Why is it similar?')}
                value={suggestReason}
                onChange={(e) => setSuggestReason(e.target.value)}
                style={{ maxWidth: '100%' }}
              />
              <SmallBtn type="submit" style={{ marginTop: '0.5rem' }}>
                {t('media.similar.submitSuggestion', 'Submit suggestion')}
              </SmallBtn>
            </form>
          )}
          {suggMsg && <p style={{ color: 'green', marginTop: '0.75rem' }}>{suggMsg}</p>}
          {suggErr && <p style={{ color: 'crimson', marginTop: '0.75rem' }}>{suggErr}</p>}
        </SuggestSection>
      </Container>

      <AnimatePresence>
        {voteModal && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeVoteModal}
          >
            <ModalCard
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <ModalClose type="button" onClick={closeVoteModal} aria-label="Close" disabled={voteSubmitting}>
                <X size={18} />
              </ModalClose>
              <ModalTitle>
                Why is <strong>{voteModal.item.title}</strong> similar to {title}?
              </ModalTitle>
              <ModalRatingLabel>Your rating: {voteModal.rating}/5</ModalRatingLabel>
              <StarRow style={{ marginBottom: 0 }}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star
                    size={22}
                    key={n}
                    fill={n <= voteModal.rating ? 'currentColor' : 'none'}
                    stroke="currentColor"
                    strokeWidth={1.5}
                    style={{ color: 'var(--gold)' }}
                  />
                ))}
              </StarRow>
              <ModalReasonLabel htmlFor="vote-reason">Your reason (optional)</ModalReasonLabel>
              <ModalTextarea
                id="vote-reason"
                value={voteReason}
                onChange={(e) => setVoteReason(e.target.value.slice(0, REASON_MAX))}
                placeholder="e.g. Same tone, pacing, and themes…"
                maxLength={REASON_MAX}
                rows={3}
                disabled={voteSubmitting || !!voteSubmittingId}
              />
              <ModalCharCount>{voteReason.length}/{REASON_MAX}</ModalCharCount>
              {voteError && <ModalError>{voteError}</ModalError>}
              <ModalActions>
                <ModalSubmitBtn
                  type="button"
                  onClick={handleSubmitVote}
                  disabled={voteSubmitting || voteSubmittingId === voteModal.item.id}
                >
                  {voteSubmitting || voteSubmittingId === voteModal.item.id ? 'Submitting…' : 'Submit vote'}
                </ModalSubmitBtn>
                <ModalCancelBtn type="button" onClick={closeVoteModal} disabled={voteSubmitting}>
                  Cancel
                </ModalCancelBtn>
              </ModalActions>
            </ModalCard>
          </ModalOverlay>
        )}
      </AnimatePresence>
      </PageLayout>
    </>
  );
}
